/**
 * Customer Videos (sections/customer-videos.liquid).
 * - A video gets its file (data-src -> src) only when its card is near the screen; nothing loads before.
 * - Only cards on screen play (at most MAX_PLAYING at once); cards off screen pause.
 * - No autoplay for reduced-motion or data-saver visitors: they see the posters and nothing downloads.
 * - Videos are always muted (also set in the HTML). Visitors scroll the row themselves: touch, trackpad, mouse drag,
 *   arrow buttons (all screen sizes), and on phones dots that follow the centred card and jump to exactly that card.
 * Video files are used exactly as served: no resizing, no re-encoding, no quality changes.
 */
(() => {
  if (customElements.get('igloo-customer-videos')) return;

  const MAX_PLAYING = 4;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);

  class IglooCustomerVideos extends HTMLElement {
    connectedCallback() {
      this.row = this.querySelector('.igloo-cv__row');
      if (!this.row) return;
      this.cards = Array.from(this.row.querySelectorAll('.igloo-cv__card:not(.igloo-cv__card--empty)'));
      this.prevBtn = this.querySelector('.igloo-cv__arrow--prev');
      this.nextBtn = this.querySelector('.igloo-cv__arrow--next');
      this.dots = Array.from(this.querySelectorAll('.igloo-cv__dot'));

      this.sectionNear = false; // within ~1 screen of the viewport: allowed to load
      this.sectionVisible = false; // on screen: allowed to play
      this.inView = new Set(); // cards inside the row's visible area
      this.paused = reduceMotion.matches || saveData; // no autoplay (and no video download) for these visitors

      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        if (!video) return;
        video.muted = true; // the muted attribute is in the HTML too
        video.addEventListener('playing', () => video.classList.add('is-ready'));
        video.addEventListener('loadeddata', () => {
          if (!this.paused) video.classList.add('is-ready');
        });
        // A file that can't play keeps the poster: hide the video, no error thrown
        video.addEventListener('error', () => video.classList.remove('is-ready'), true);
      });

      this.observe();
      this.bindArrows();
      this.bindDots();
      this.bindDrag();

      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateArrows();
          this.updateDots();
        });
      };
      this.row.addEventListener('scroll', this.onScroll, { passive: true });
      this.onResize = () => this.onScroll();
      window.addEventListener('resize', this.onResize);
      this.updateArrows();
      this.updateDots();
    }

    disconnectedCallback() {
      this.sectionObserver?.disconnect();
      this.nearObserver?.disconnect();
      this.cardObserver?.disconnect();
      this.cardViewObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      this.cards?.forEach((card) => card.querySelector('video')?.pause());
    }

    observe() {
      this.nearObserver = new IntersectionObserver(
        (entries) => {
          this.sectionNear = entries[entries.length - 1].isIntersecting;
          this.sync();
        },
        { rootMargin: '600px 0px' }
      );
      this.nearObserver.observe(this);

      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          this.sectionVisible = entries[entries.length - 1].isIntersecting;
          this.sync();
        },
        { threshold: 0.15 }
      );
      this.sectionObserver.observe(this.row);

      // Near: within 10% of the row's width beyond its visible edges (the next card) -> its file is attached (metadata only)
      this.cardObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.target.dataset.near = entry.isIntersecting ? '1' : '';
          });
          this.sync();
        },
        { root: this.row, rootMargin: '0px 10%' }
      );
      // On screen: at least half the card inside the row's visible area -> it may play
      this.cardViewObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) this.inView.add(entry.target);
            else this.inView.delete(entry.target);
          });
          this.sync();
        },
        { root: this.row, threshold: [0, 0.5] }
      );
      this.cards.forEach((card) => {
        this.cardObserver.observe(card);
        this.cardViewObserver.observe(card);
      });
    }

    // Near the screen: attach the file but fetch only its metadata. Playing: let the browser buffer it.
    load(video, playing = false) {
      if (!video.dataset.src) return;
      if (!video.getAttribute('src')) {
        video.preload = playing ? 'auto' : 'metadata';
        video.src = video.dataset.src;
      } else if (playing && video.preload !== 'auto') {
        video.preload = 'auto';
      }
    }

    sync() {
      const playing = [];
      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        if (!video) return;
        const near = this.sectionNear && card.dataset.near === '1';
        const wantsPlay = !this.paused && this.sectionVisible && this.inView.has(card) && playing.length < MAX_PLAYING;
        // Reduced motion / data saver: posters only, nothing loads
        if (near && !this.paused) this.load(video);
        if (wantsPlay) {
          playing.push(video);
          this.load(video, true);
          const attempt = video.play();
          if (attempt && attempt.catch) attempt.catch(() => {}); // autoplay refused: the poster stays
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    /* ---------- Arrows ---------- */
    step() {
      const card = this.cards[0];
      if (!card) return this.row.clientWidth;
      const gap = parseFloat(getComputedStyle(this.row).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    bindArrows() {
      this.prevBtn?.addEventListener('click', () => this.row.scrollBy({ left: -this.step(), behavior: this.behavior() }));
      this.nextBtn?.addEventListener('click', () => this.row.scrollBy({ left: this.step(), behavior: this.behavior() }));
    }

    updateArrows() {
      if (!this.prevBtn || !this.nextBtn) return;
      const max = this.row.scrollWidth - this.row.clientWidth;
      this.prevBtn.hidden = this.row.scrollLeft <= 2;
      this.nextBtn.hidden = this.row.scrollLeft >= max - 2;
    }

    behavior() {
      return reduceMotion.matches ? 'auto' : 'smooth';
    }

    /* ---------- Dots (mobile) ---------- */
    centredIndex() {
      const rowBox = this.row.getBoundingClientRect();
      const centre = rowBox.left + rowBox.width / 2;
      let best = 0;
      let bestDistance = Infinity;
      this.cards.forEach((card, i) => {
        const box = card.getBoundingClientRect();
        const distance = Math.abs(box.left + box.width / 2 - centre);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    }

    updateDots() {
      if (!this.dots.length) return;
      const active = this.centredIndex();
      this.dots.forEach((dot, i) => {
        if (i === active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    scrollToCard(index) {
      const card = this.cards[index];
      if (!card) return;
      // Centre exactly this card in the row (the row's own padding lets the first and last cards centre too)
      const left = card.offsetLeft - (this.row.clientWidth - card.offsetWidth) / 2;
      this.row.scrollTo({ left, behavior: this.behavior() });
    }

    bindDots() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.scrollToCard(Number(dot.dataset.index)));
      });
    }

    /* ---------- Mouse drag (touch and trackpads scroll natively) ---------- */
    bindDrag() {
      let startX = 0;
      let startScroll = 0;
      let moved = false;
      let pointerId = null;

      this.row.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = this.row.scrollLeft;
        moved = false;
      });

      this.row.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) < 5) return;
        if (!moved) {
          moved = true;
          this.row.classList.add('is-dragging');
          this.row.setPointerCapture(pointerId);
        }
        this.row.scrollLeft = startScroll - dx;
      });

      const end = (event) => {
        if (event.pointerId !== pointerId) return;
        pointerId = null;
        if (!moved) return;
        // Let snapping settle on the nearest card again
        const before = this.row.scrollLeft;
        this.row.classList.remove('is-dragging');
        const after = this.row.scrollLeft;
        this.row.scrollLeft = before;
        this.row.scrollTo({ left: after, behavior: this.behavior() });
      };
      this.row.addEventListener('pointerup', end);
      this.row.addEventListener('pointercancel', end);
      // A drag is not a click
      this.row.addEventListener(
        'click',
        (event) => {
          if (moved) {
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          }
        },
        true
      );
      this.row.addEventListener('dragstart', (event) => event.preventDefault());
    }
  }

  customElements.define('igloo-customer-videos', IglooCustomerVideos);
})();
